import { FilterSidebar } from "@/components/layout/FilterSidebar";
import { NewSearchBar } from "@/components/layout/NewSearchBar";
import { PageContainer } from "@/components/layout/PageContainer";
import { PageHeader } from "@/components/layout/PageHeader";
import Seo from "@/components/seo/Seo";
import { HelpRequestButton } from "@/components/HelpRequestButton";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { useRegion } from "@/contexts/RegionContext";
import { toArray } from "@/lib/array";
import { normalizeImageUrl } from "@/lib/image-url";
import {
  searchCategories as allSearchCategories,
  type SearchCategory,
} from "@/lib/searchCategories";
import { communityTalkCategories, type CommunityTalk } from "@shared/schema";
import {
  getCommunityContentIntent,
  getCommunityContentIntentLabel,
  type CommunityIntentRecord,
} from "@shared/community-content-intent";
import type { TopicContentIntent } from "@shared/topic-post-metadata";
import { australianStates, suburbsByState } from "@shared/suburbs";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { BookOpen, Clock, Filter, MessageCircleHeart, Plus, Users, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";

// 전체 카테고리 (market과 동일)
const marketCategories: SearchCategory[] = [
  allSearchCategories.find((c) => c.id === "community")!,
  allSearchCategories.find((c) => c.id === "community-talk")!,
  allSearchCategories.find((c) => c.id === "real-estate")!,
  allSearchCategories.find((c) => c.id === "used-car")!,
  allSearchCategories.find((c) => c.id === "job")!,
  allSearchCategories.find((c) => c.id === "meetup")!,
].filter(Boolean);

export default function CommunityTalkPage() {
  const {
    selectedRegion,
    selectedSuburb,
    setSelectedRegion,
    setSelectedSuburb,
  } = useRegion();
  const [location, navigate] = useLocation();
  const [selectedMainCategory] = useState<SearchCategory>(marketCategories[1]); // 동네톡톡이 기본
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedIntent, setSelectedIntent] = useState<"all" | "legacy" | TopicContentIntent>(() => {
    if (typeof window === "undefined") return "all";
    const intent = new URLSearchParams(window.location.search).get("intent");
    return intent === "question" || intent === "experience" || intent === "legacy"
      ? intent
      : "all";
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  useEffect(() => {
    const intent = new URLSearchParams(
      typeof window === "undefined" ? "" : window.location.search,
    ).get("intent");
    setSelectedIntent(
      intent === "question" || intent === "experience" || intent === "legacy"
        ? intent
        : "all",
    );
  }, [location]);

  // 카테고리 클릭 핸들러
  const handleCategoryClick = (category: SearchCategory) => {
    if (category.id === "community-talk") {
      // 이미 동네톡톡 페이지에 있음
      return;
    } else if (category.id === "community") {
      // 동네마켓으로 이동
      navigate("/market");
    } else {
      // 다른 카테고리 클릭 시 market으로 카테고리 파라미터와 함께 이동
      navigate(`/market?category=${category.id}`);
    }
  };

  // 카테고리별 인기 검색어
  const getPopularKeywords = () => {
    return ["맛집", "동네행사", "반려동물", "운동", "생활/편의", "동네친구"];
  };

  // Build query URL
  const queryUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (selectedRegion !== "ALL") params.append("region", selectedRegion);
    if (selectedSuburb) params.append("suburb", selectedSuburb);
    if (selectedCategory !== "all") params.append("category", selectedCategory);
    if (searchTerm) params.append("q", searchTerm);
    const queryString = params.toString();
    return queryString
      ? `/api/community-talk?${queryString}`
      : "/api/community-talk";
  }, [selectedRegion, selectedSuburb, selectedCategory, searchTerm]);

  // Fetch community talk posts
  const { data: rawPosts, isLoading } = useQuery({
    queryKey: [queryUrl],
  });

  // Safe array conversion - prevents ".filter() is not a function" errors
  const allPosts = toArray<CommunityTalk & CommunityIntentRecord>(rawPosts);
  const posts = allPosts.filter(
    (post) =>
      selectedIntent === "all" ||
      (selectedIntent === "legacy"
        ? getCommunityContentIntent(post) === undefined
        : getCommunityContentIntent(post) === selectedIntent),
  );
  const intentCounts = {
    all: allPosts.length,
    question: allPosts.filter((post) => getCommunityContentIntent(post) === "question").length,
    experience: allPosts.filter((post) => getCommunityContentIntent(post) === "experience").length,
    legacy: allPosts.filter((post) => getCommunityContentIntent(post) === undefined).length,
  };

  // 지역 표시 이름 매핑 (주요 도시 이름 우선)
  const getDisplayLocation = () => {
    if (selectedSuburb) {
      const suburbInfo = Object.values(suburbsByState)
        .flat()
        .find((s) => s.id === selectedSuburb);
      return suburbInfo?.nameKo || selectedSuburb;
    }

    const stateInfo = australianStates.find((s) => s.id === selectedRegion);
    if (!stateInfo || selectedRegion === "ALL") {
      return "호주 전역";
    }

    // 주요 도시 이름 매핑
    const majorCityMap: Record<string, string> = {
      NSW: "시드니",
      VIC: "멜번",
      QLD: "브리즈번",
      SA: "아델레이드",
      WA: "퍼스",
      NT: "다윈",
      ACT: "캔버라",
      TAS: "호바트",
    };

    return majorCityMap[selectedRegion] || stateInfo.nameKo || "전체 지역";
  };

  const displayLocation = getDisplayLocation();

  const selectIntent = (intent: "all" | "legacy" | TopicContentIntent) => {
    setSelectedIntent(intent);

    const params = new URLSearchParams(
      typeof window === "undefined" ? "" : window.location.search,
    );
    if (intent === "all") {
      params.delete("intent");
    } else {
      params.set("intent", intent);
    }
    const query = params.toString();
    navigate(query ? `/community-talk?${query}` : "/community-talk");
  };

  return (
    <>
      <Seo
        title="동네 톡톡 — 우리 동네 이야기·질문·해결 경험 | OZK"
        description="호주 한인 이웃과 편하게 동네 이야기를 나누고, 공감과 댓글로 응원하거나 생활문제 해결책을 주고받아 보세요."
        path="/community-talk"
        keywords={[
          "동네 톡톡",
          "호주 한인 동네생활",
          "호주 한인 커뮤니티",
          "생활문제 해결",
        ]}
      />

      <div className="min-h-screen bg-background">
        {/* Search Section - NewSearchBar 사용 */}
        <NewSearchBar
          defaultCategory="community-talk"
          onSearch={(query: string) => {
            setSearchQuery(query);
            setSearchTerm(query);
          }}
        />

        {/* Main Content */}
        <PageContainer size="default">
          <div className="space-y-5">
            <PageHeader
              title={`${displayLocation} 동네 톡톡`}
              description="가벼운 동네 소식과 일상 이야기부터 궁금한 점과 생활문제 해결 경험까지 편하게 나누세요. 이웃이 공감하고 댓글로 응원하거나 해결책을 보탤 수 있습니다."
              actions={<div className="flex flex-wrap gap-2"><Button onClick={() => navigate("/community-talk/new")} size="sm" data-testid="button-create-talk"><Plus className="mr-1 h-4 w-4" />이야기 쓰기</Button><Button onClick={() => navigate("/questions/new")} size="sm" variant="outline" data-testid="button-create-question">질문하기</Button><Button onClick={() => navigate("/experiences/new")} size="sm" variant="outline" data-testid="button-create-experience">해결 경험</Button></div>}
            />
            <div className="grid gap-3 md:grid-cols-2">
              <Link href="/topics">
                <Card className="h-full border-primary/20 bg-primary/5 p-4 transition-colors hover:border-primary/40">
                  <div className="flex items-start gap-3">
                    <span className="rounded-lg bg-background p-2 text-primary"><BookOpen className="h-5 w-5" /></span>
                    <div><p className="font-semibold text-foreground">생활문제 해결 가이드</p><p className="mt-1 text-sm leading-6 text-muted-foreground">본드, 비자, 세금처럼 정확한 절차가 필요한 문제는 검증된 안내부터 확인하세요.</p></div>
                  </div>
                </Card>
              </Link>
              <Link href="/meetings">
                <Card className="h-full p-4 transition-colors hover:border-primary/40">
                  <div className="flex items-start gap-3">
                    <span className="rounded-lg bg-primary/10 p-2 text-primary"><Users className="h-5 w-5" /></span>
                    <div><p className="font-semibold text-foreground">교민모임</p><p className="mt-1 text-sm leading-6 text-muted-foreground">관심사가 같은 교민을 찾고 오프라인 모임과 활동에 참여하세요.</p></div>
                  </div>
                </Card>
              </Link>
            </div>
            <Card className="border-dashed p-4">
              <div className="flex items-center gap-3">
                <MessageCircleHeart className="h-5 w-5 shrink-0 text-primary" />
                <p className="text-sm leading-6 text-muted-foreground"><strong className="text-foreground">공감하고 응원해주세요.</strong> 좋아요와 댓글, 답글로 경험과 해결책을 나눌 수 있습니다.</p>
              </div>
            </Card>
            <div className="flex flex-wrap items-center gap-2" aria-label="작성 목적 필터">
              <span className="mr-1 text-sm font-medium text-foreground">글 유형</span>
              {([
                ["all", "전체"],
                ["question", "질문"],
                ["experience", "해결 경험"],
                ["legacy", "동네 이야기"],
              ] as const).map(([value, label]) => (
                <Button
                  key={value}
                  type="button"
                  size="sm"
                  variant={selectedIntent === value ? "default" : "outline"}
                  onClick={() => selectIntent(value)}
                  aria-pressed={selectedIntent === value}
                  data-testid={`filter-intent-${value}`}
                >
                  {label} {intentCounts[value]}
                </Button>
              ))}
            </div>
            <div className="flex items-center justify-between gap-3 lg:hidden">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowMobileFilters(!showMobileFilters)}
                  className="lg:hidden"
                  data-testid="button-mobile-filter-toggle"
                >
                  <Filter className="w-4 h-4 mr-1" />
                  필터
                </Button>
            </div>

            <div className="ozk-list-layout">
              {/* Sidebar - Filters (Desktop) - FilterSidebar 사용 */}
              <FilterSidebar
                sections={[
                  {
                    id: "category",
                    title: "카테고리",
                    type: "radio",
                    options: [
                      { id: "all", label: "전체", value: "all" },
                      ...communityTalkCategories.map((c) => ({
                        id: c.id,
                        label: c.label,
                        value: c.id,
                      })),
                    ],
                    selected: selectedCategory,
                    onSelect: (value) => setSelectedCategory(value as string),
                  },
                ]}
                onReset={() => {
                  setSelectedCategory("all");
                  selectIntent("all");
                }}
              />

              {/* Mobile Filters */}
              {showMobileFilters && (
                <div
                  className="fixed inset-0 z-[70] flex items-end bg-black/50 lg:hidden"
                  role="dialog"
                  aria-modal="true"
                  aria-label="생활 질문과 경험 필터"
                  onClick={() => setShowMobileFilters(false)}
                >
                  <Card
                    className="max-h-[82vh] w-full overflow-y-auto rounded-b-none rounded-t-2xl border-b-0"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <div className="p-6 space-y-6">
                      <div className="flex items-center justify-between">
                        <h2 className="font-semibold text-lg">필터</h2>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setShowMobileFilters(false)}
                          data-testid="button-close-mobile-filters"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* 카테고리 필터 */}
                      <div className="space-y-3">
                        <h3 className="text-sm font-medium">카테고리</h3>
                        <RadioGroup
                          value={selectedCategory}
                          onValueChange={setSelectedCategory}
                          className="space-y-1"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="all"
                              id="category-all-mobile"
                            />
                            <Label
                              htmlFor="category-all-mobile"
                              className="cursor-pointer text-sm font-normal leading-tight"
                            >
                              전체
                            </Label>
                          </div>
                          {communityTalkCategories.map((category) => (
                            <div
                              key={category.id}
                              className="flex items-center space-x-2"
                            >
                              <RadioGroupItem
                                value={category.id}
                                id={`category-${category.id}-mobile`}
                              />
                              <Label
                                htmlFor={`category-${category.id}-mobile`}
                                className="cursor-pointer text-sm font-normal leading-tight"
                              >
                                {category.label}
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      </div>
                      <div className="sticky bottom-0 mt-4 grid grid-cols-2 gap-2 border-t bg-card pt-4">
                        <Button variant="outline" onClick={() => { setSelectedCategory("all"); selectIntent("all"); }}>초기화</Button>
                        <Button onClick={() => setShowMobileFilters(false)}>결과 보기</Button>
                      </div>
                    </div>
                  </Card>
                </div>
              )}

              {/* Main List */}
              <main className="ozk-list-main">
                {/* Posts List */}
                {isLoading ? (
                  <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-32 w-full" />
                    ))}
                  </div>
                ) : posts.length > 0 ? (
                  <div className="space-y-0">
                    {posts.map((post) => {
                      const categoryInfo = communityTalkCategories.find(
                        (c) => c.id === post.category
                      );

                      return (
                        <Link
                          key={post.id}
                          href={`/community-talk/${post.id}`}
                          data-testid={`post-${post.id}`}
                        >
                          <div className="pb-4 border-b border-border/30 cursor-pointer transition-colors last:border-b-0">
                            <div className="flex gap-4">
                              {/* Thumbnail */}
                              {post.imageUrls && post.imageUrls.length > 0 && (
                                <div className="flex-shrink-0">
                                  <img
                                    src={
                                      normalizeImageUrl(post.imageUrls[0]) ||
                                      post.imageUrls[0]
                                    }
                                    alt={post.title}
                                    className="w-20 h-20 object-cover rounded-md"
                                  />
                                </div>
                              )}

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <span className="mb-1 inline-flex rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                                  {getCommunityContentIntentLabel(getCommunityContentIntent(post))}
                                </span>
                                {/* Title */}
                                <h3 className="font-semibold text-base mb-1 line-clamp-2 text-foreground">
                                  {post.title}
                                </h3>

                                {/* Summary */}
                                <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                                  {post.content?.substring(0, 100) || ""}
                                  {post.content && post.content.length > 100
                                    ? "..."
                                    : ""}
                                </p>

                                {/* Meta Info - Category, Location, Time */}
                                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                  <div className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {formatDistanceToNow(
                                      new Date(post.createdAt),
                                      {
                                        addSuffix: true,
                                        locale: ko,
                                      }
                                    )}
                                  </div>
                                  {categoryInfo && (
                                    <>
                                      <span className="text-muted-foreground/60">
                                        ·
                                      </span>
                                      <span className="text-muted-foreground">
                                        {categoryInfo.label}
                                      </span>
                                    </>
                                  )}
                                  {post.suburb && (
                                    <>
                                      <span className="text-muted-foreground/60">
                                        ·
                                      </span>
                                      <span className="text-muted-foreground">
                                        {post.suburb}
                                      </span>
                                    </>
                                  )}
                                  {post.likes !== undefined &&
                                    post.likes !== null &&
                                    post.likes > 0 && (
                                      <>
                                        <span className="text-muted-foreground/60">
                                          ·
                                        </span>
                                        <span className="text-muted-foreground">
                                          좋아요 {post.likes}
                                        </span>
                                      </>
                                    )}
                                  {post.commentCount !== undefined &&
                                    post.commentCount !== null &&
                                    post.commentCount > 0 && (
                                      <>
                                        <span className="text-muted-foreground/60">
                                          ·
                                        </span>
                                        <span className="text-muted-foreground">
                                          댓글 {post.commentCount}
                                        </span>
                                      </>
                                    )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <Card className="p-12 text-center">
                    <h3 className="text-lg font-semibold">
                      {selectedIntent === "question"
                        ? "조건에 맞는 질문이 없습니다."
                        : selectedIntent === "experience"
                          ? "조건에 맞는 해결 경험이 없습니다."
                          : selectedIntent === "legacy"
                            ? "아직 동네 이야기가 없습니다."
                          : "아직 동네 톡톡 글이 없습니다."}
                    </h3>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                      {selectedIntent === "question"
                        ? "궁금한 상황과 이미 시도한 내용을 구체적으로 질문해보세요."
                        : selectedIntent === "experience"
                          ? "직접 해결한 과정과 결과를 다음 사람을 위해 공유해보세요."
                          : selectedIntent === "legacy"
                            ? "가벼운 일상, 동네 소식이나 이웃과 나누고 싶은 이야기를 먼저 남겨보세요."
                          : "동네 이야기를 나누거나, 궁금한 상황을 질문하고 해결 경험을 공유해보세요."}
                    </p>
                    <div className="mt-5 flex flex-col justify-center gap-2 sm:flex-row">
                      {selectedIntent !== "experience" && selectedIntent !== "legacy" && (
                        <Button onClick={() => navigate("/questions/new")}>질문하기</Button>
                      )}
                      {selectedIntent !== "question" && selectedIntent !== "legacy" && (
                        <Button variant="outline" onClick={() => navigate("/experiences/new")}>해결 경험 공유</Button>
                      )}
                      {selectedIntent === "legacy" && (
                        <Button onClick={() => navigate("/community-talk/new")}>이야기 쓰기</Button>
                      )}
                    </div>
                    {selectedIntent !== "legacy" && (
                      <div className="mt-4 flex justify-center">
                        <HelpRequestButton
                          sourceType="community_talk"
                          zoneId={selectedRegion !== "ALL" ? selectedRegion : undefined}
                          suburb={selectedSuburb || undefined}
                        />
                      </div>
                    )}
                  </Card>
                )}
              </main>
            </div>
          </div>
        </PageContainer>
      </div>
    </>
  );
}
